import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import { type BalancesResponse } from "../utils/types/BalanceService.types";
import {
  type BitcoinHdWalletBalancesResponse,
  type BitcoinTransactionResponse,
  type GetBitcoinHdWalletBalancesQueryParamOpts,
  type GetBitcoinNonHdWalletBalancesQueryParamOpts,
  type GetTransactionsForBitcoinAddressParamOpts,
} from "../utils/types/BitcoinService.types";
import { ChainName, type GoldRushResponse } from "../utils/types/Generic.types";

/**
 * Bitcoin APIs
 *
 */
export class BitcoinService {
  constructor(private execution: Execution) {}

  /**
   *
   * @param {string} walletAddress - The requested Bitcoin HD address.
   * @param {GetBitcoinHdWalletBalancesQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *
   */
  public async getBitcoinHdWalletBalances(
    walletAddress: string,
    queryParamOpts?: GetBitcoinHdWalletBalancesQueryParamOpts
  ): Promise<GoldRushResponse<BitcoinHdWalletBalancesResponse>> {
    const endpoint = endpointGenerator(
      `${ChainName.BTC_MAINNET}/address/${walletAddress}/hd_wallets`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
      ]
    );

    const parseData = (
      data: GoldRushResponse<BitcoinHdWalletBalancesResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((balanceItem) => ({
              ...balanceItem,
              balance: bigIntParser(balanceItem.balance),
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<BitcoinHdWalletBalancesResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Used to fetch the full transaction history of a Bitcoin wallet.
   *
   * @param {GetTransactionsForBitcoinAddressParamOpts} queryParamOpts
   *   - `address`: The bitcoin address to query.
   *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
   *   - `pageNumber`: 0-indexed page number to begin pagination.
   */
  public async getTransactionsForBtcAddress(
    queryParamOpts?: GetTransactionsForBitcoinAddressParamOpts
  ): Promise<GoldRushResponse<BitcoinTransactionResponse>> {
    const endpoint = endpointGenerator(
      `cq/covalent/app/bitcoin/transactions/`,
      [
        {
          key: "address",
          value: queryParamOpts?.address,
        },
        {
          key: "page-size",
          value: queryParamOpts?.pageSize,
        },
        {
          key: "page-number",
          value: queryParamOpts?.pageNumber,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<BitcoinTransactionResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((txItem) => ({
              ...txItem,
              value: bigIntParser(txItem.value),
              fees_paid: bigIntParser(txItem.fees_paid),
              block_signed_at: txItem.block_signed_at
                ? new Date(txItem.block_signed_at)
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<BitcoinTransactionResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to fetch the tokens held by an address. Response includes spot prices and other metadata.
   *
   * @param {string} walletAddress - The requested Bitcoin Non HD address.
   * @param {GetBitcoinNonHdWalletBalancesQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *
   */
  public async getBitcoinNonHdWalletBalances(
    walletAddress: string,
    queryParamOpts?: GetBitcoinNonHdWalletBalancesQueryParamOpts
  ): Promise<GoldRushResponse<BalancesResponse>> {
    const endpoint = endpointGenerator(
      `${ChainName.BTC_MAINNET}/address/${walletAddress}/balances_v2`,
      [
        {
          key: "quote-currency",
          value: queryParamOpts?.quoteCurrency,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<BalancesResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((balanceItem) => ({
              ...balanceItem,
              balance: bigIntParser(balanceItem.balance),
              balance_24h: bigIntParser(balanceItem.balance_24h),
              last_transferred_at: balanceItem.last_transferred_at
                ? new Date(balanceItem.last_transferred_at)
                : null,
              nft_data: balanceItem.nft_data
                ? balanceItem.nft_data.map((nftItem) => ({
                    ...nftItem,
                    token_id: bigIntParser(nftItem.token_id),
                    token_balance: bigIntParser(nftItem.token_balance),
                    token_price_wei: bigIntParser(nftItem.token_price_wei),
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<BalancesResponse>(endpoint, parseData);
  }
}
